/**
 * inferenceLoader — convert `shop_setup_inferences` +
 * `technician_inferred_profile` rows into the LeanSetupWizard's
 * `initial: Partial<WizardData>` shape.
 *
 * IRON-CLAD RULES
 * ---------------
 *   1. PURE FUNCTION. Takes typed inputs, returns typed output.
 *      No Supabase calls, no DOM, no React.
 *   2. ADVISORY ONLY. The output is the wizard's STARTING POINT,
 *      not a commit. The wizard's Finish-Setup button remains the
 *      only live shop-config commit point.
 *   3. NEVER GUESSES. Low-confidence or missing inferences fall
 *      back to the wizard's existing manual-mode defaults — we do
 *      not invent numbers the inference didn't produce.
 *   4. CONFIDENCE THRESHOLD. Per the Phase 2.1 helper contract,
 *      <0.4 confidence is "informational only" — the loader skips
 *      those inferences entirely so the wizard doesn't prefill
 *      with low-evidence noise.
 */

import type {
  Page1Data,
  Page2Data,
  Page3Data,
  WizardData,
  SpecialtyKind,
} from './wizardValidation';
import type { BayTypeEnum } from '@/utils/bayTypeLabels';

// =================================================================
// Input row shapes — match the migration columns.
// =================================================================

export interface SetupInferenceRow {
  inference_kind:
    | 'shop_hours'
    | 'technician_roster'
    | 'service_mix'
    | 'vehicle_mix'
    | 'parking_heuristic';
  confidence: number;
  payload: Record<string, unknown>;
}

export interface TechnicianInferredProfileRow {
  external_technician_id: string;
  display_name: string;
  job_count: number;
  total_labor_hours: number;
  median_labor_hours: number | null;
  comeback_rate: number;
  warranty_rate: number;
  appears_full_time: boolean;
  top_jobs: Array<{ description: string; count: number }> | null;
}

export interface InferenceLoaderInput {
  inferences: SetupInferenceRow[];
  technicianProfiles: TechnicianInferredProfileRow[];
  /** The shop name pulled from the shops row at load time. */
  shopName: string;
  /** Shop tz — snapshotted on the import_run row. */
  timeZone: string;
  /**
   * Batch 9B: distinct advisor names sourced from
   * shop_history_repair_orders.advisor_display_name, ordered by
   * frequency descending. Optional — callers without access to the
   * permanent table simply pass nothing and SERVICE_WRITER rows
   * stay blank.
   */
  advisorNames?: string[];
}

// =================================================================
// Constants
// =================================================================

const CONFIDENCE_THRESHOLD = 0.4;

/**
 * Core-100 R4 (CORE-U, SP-05): the setup values the wizard prefilled FROM HISTORY. Each one is presented as an
 * inference and must be confirmed by the person before it becomes shop truth — commit_shop_setup refuses an
 * import-sourced setup with any of these unconfirmed (INFERENCE_NOT_CONFIRMED). Mirrors exactly the gates the page
 * builders below use to decide whether an inference prefilled a value.
 */
export interface InferredSetupField {
  key: 'hours' | 'technician_roster' | 'parking_spot_count';
  label: string;
}
export function inferredSetupFields(inferences: SetupInferenceRow[]): InferredSetupField[] {
  const out: InferredSetupField[] = [];
  const hours = inferences.find((i) => i.inference_kind === 'shop_hours');
  if (hours && hours.confidence >= CONFIDENCE_THRESHOLD) out.push({ key: 'hours', label: 'Opening hours' });
  if (inferences.some((i) => i.inference_kind === 'technician_roster')) {
    out.push({ key: 'technician_roster', label: 'Number of technicians and their names' });
  }
  const parking = inferences.find((i) => i.inference_kind === 'parking_heuristic');
  if (parking && parking.confidence >= CONFIDENCE_THRESHOLD) out.push({ key: 'parking_spot_count', label: 'Number of parking spots' });
  return out;
}

const DEFAULT_DAY_HOURS = { open: '08:00', close: '17:00' } as const;

const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

// No legacy Apprentice / Technician / Foreman mapping: `technicians.role`
// is a server-filled compatibility column, not something onboarding infers
// or a manager chooses. See LEGACY_TECHNICIAN_ROLE_DOCTRINE.

// =================================================================
// Page 1 — Shop hours from `shop_hours` inference
// =================================================================

interface ShopHoursPayload {
  byWeekday: Array<{
    weekday: number;
    openHHMM: string | null;
    closeHHMM: string | null;
    sampleSize: number;
  }>;
}

/**
 * Optional override carried by the loader so Page 1 can respect
 * hours that were already populated by a public-listing /
 * grounded-search lookup (Batch 8B). When provided, CSV inference
 * never displaces them — the operator's authoritative answer wins.
 */
export interface BuildPage1ExistingHours {
  hours: Record<string, { open: string; close: string }> | null;
  source: string | null;
}

function buildPage1(
  shopName: string,
  timeZone: string,
  inferences: SetupInferenceRow[],
  existingHours?: BuildPage1ExistingHours,
): Page1Data {
  // Batch 8B precedence:
  //   1. existing public-listing / grounded-search / manual hours on
  //      shop_profile → use verbatim, CSV cannot override.
  //   2. CSV inference (confidence ≥ threshold) → fill missing days.
  //   3. Default Mon-Fri 8-5.
  const providerOwned =
    existingHours?.source === 'public_listing'
    || existingHours?.source === 'grounded_search'
    || existingHours?.source === 'manual';
  if (providerOwned && existingHours?.hours && Object.keys(existingHours.hours).length > 0) {
    return { name: shopName, time_zone: timeZone, hours: existingHours.hours };
  }

  const hoursInference = inferences.find((i) => i.inference_kind === 'shop_hours');
  const hours: Record<string, { open: string; close: string }> = {};

  if (hoursInference && hoursInference.confidence >= CONFIDENCE_THRESHOLD) {
    const payload = hoursInference.payload as unknown as ShopHoursPayload;
    for (const dayBucket of payload.byWeekday ?? []) {
      if (
        dayBucket.sampleSize > 0 &&
        dayBucket.openHHMM &&
        dayBucket.closeHHMM &&
        // Sanity: open < close, both HH:MM. We accept the values
        // verbatim from the helper; the wizard's zod schema will
        // re-validate before commit.
        dayBucket.openHHMM < dayBucket.closeHHMM
      ) {
        const key = DAY_KEYS[dayBucket.weekday];
        if (key) hours[key] = { open: dayBucket.openHHMM, close: dayBucket.closeHHMM };
      }
    }
  }

  // If no usable inference, seed Mon-Fri 8-5. The wizard re-validates
  // on submit, and Batch 8B's relaxed `page1Schema` now treats hours
  // as optional anyway — but a populated default still gives the
  // operator something to glance at and accept.
  if (Object.keys(hours).length === 0) {
    for (const d of ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']) {
      hours[d] = { ...DEFAULT_DAY_HOURS };
    }
  }

  return {
    name: shopName,
    time_zone: timeZone,
    hours,
  };
}

// =================================================================
// Page 2 — People counts from `technician_roster` inference
// =================================================================

interface TechRosterPayload {
  technicians: Array<{
    externalTechnicianId: string;
    displayName: string;
    jobCount: number;
    appearsFullTime: boolean;
  }>;
}

/**
 * Batch 9C: maximum auto-suggested technician count. CSVs can carry
 * decades of names and a handful of typo variants; capping at 20
 * stops the wizard from rendering hundreds of rows when an old DMS
 * exports every name it ever recorded. Real shops above this size
 * can use Add Team Member after onboarding.
 */
const MAX_SUGGESTED_TECH_COUNT = 20;

function buildPage2(inferences: SetupInferenceRow[]): Page2Data {
  // Batch 9C: drop the confidence gate. The roster's distinct-name
  // count is a useful default even at low inference confidence —
  // names came from the CSV's actual `Technician Name` column.
  // appearsFullTime is a separate, narrower signal that's still
  // surfaced on the inference review card; it should not block the
  // initial Step 2 count. The operator can edit the count up or down
  // and the Step 3 reconciler now tops up from the same roster, so
  // overshooting here is recoverable.
  const rosterInference = inferences.find((i) => i.inference_kind === 'technician_roster');
  let numTechnicians = 0;
  if (rosterInference) {
    const payload = rosterInference.payload as unknown as TechRosterPayload;
    numTechnicians = Math.min(
      (payload.technicians ?? []).length,
      MAX_SUGGESTED_TECH_COUNT,
    );
  }
  // Floor at the wizard's required minimum (1).
  if (numTechnicians < 1) numTechnicians = 1;

  return {
    num_technicians: numTechnicians,
    // Advisors and managers are not inferable from historical
    // imports today — leave at zero so the wizard's Page 2 prompts
    // the user.
    num_advisors: 0,
    num_managers: 0,
  };
}

// =================================================================
// Page 3 — Team, bays, specialty, parking
// =================================================================

interface ParkingPayload {
  suggestedSpotCount: number;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/**
 * Batch 9: conservative starter skill from `comeback_rate`. Floor 4,
 * ceiling 6 — we never over- or under-claim a starter tier without
 * supervisor input. Anyone with a 0% comeback rate AND a meaningful
 * sample size (>= 10 jobs) lands at 6; very high comeback rate (>
 * 10%) lands at 4; everything else at 5.
 */
function starterSkillTier(profile: TechnicianInferredProfileRow | undefined): number {
  if (!profile) return 5;
  if (profile.job_count < 10) return 5;
  if (profile.comeback_rate <= 0.01) return 6;
  if (profile.comeback_rate > 0.1) return 4;
  return 5;
}

function buildPage3(
  inferences: SetupInferenceRow[],
  technicianProfiles: TechnicianInferredProfileRow[],
  page2: Page2Data,
  // Batch 9B: distinct advisor names sourced from the permanent
  // shop_history_repair_orders.advisor_display_name column. The CSV
  // parser captures advisorDisplayName but the inference layer does
  // not (yet) aggregate it. We pass the raw distinct list here so
  // the wizard's SERVICE_WRITER rows prefill the same way technician
  // rows do. Caller orders by frequency desc; we trust the order.
  advisorNames: string[] = [],
): Page3Data {
  // Batch 9: surface ALL roster technicians (not just full-time) so
  // names the inference review page already showed don't disappear
  // on Step 3. Sort by job_count descending so the most active techs
  // appear first; cap at page2.num_technicians.
  //
  // Batch 9B: drop the confidence gate. The previous gate
  // (`confidence >= 0.4`) was preventing the small-mock-CSV happy
  // path from prefilling any names — confidence is a function of
  // total job count, and a small but legitimate CSV (e.g., 30 jobs)
  // lands at 0.30. The inference review card already shows the names
  // regardless of confidence; the wizard's Step 3 should be
  // consistent. The operator can delete or edit any prefilled row.
  const rosterInference = inferences.find((i) => i.inference_kind === 'technician_roster');
  let rosterTechnicians: Array<{ externalTechnicianId: string; displayName: string; jobCount: number }> = [];
  if (rosterInference) {
    const payload = rosterInference.payload as unknown as TechRosterPayload;
    rosterTechnicians = [...(payload.technicians ?? [])].sort(
      (a, b) => (b.jobCount ?? 0) - (a.jobCount ?? 0),
    );
  }

  const profilesById = new Map(technicianProfiles.map((p) => [p.external_technician_id, p]));
  // Batch 16-B: every inferred tech row starts with one Flat bay so the
  // wizard renders a populated per-bay-type dropdown immediately and
  // commitWizard writes the canonical 'flat' enum value (not the legacy
  // 'general' alias) when the operator doesn't pick a different shape.
  const techs = rosterTechnicians
    .slice(0, page2.num_technicians)
    .map((r) => {
      const profile = profilesById.get(r.externalTechnicianId);
      const name = profile?.display_name ?? r.displayName;
      return {
        name,
        skill_tier: starterSkillTier(profile),
        efficiency_percentage: 100,
        // B-W4: an imported roster carries no buffer — "No buffer set" (planned as 0 %), not a recorded 0.
        buffer_percentage: null as number | null,
        shift_start: '08:00',
        shift_end: '17:00',
        bays_allocated: 1,
        bay_types: ['flat'] as BayTypeEnum[],
      };
    });

  // Top up to match Page 2's num_technicians count. Extras are
  // blank-named — the user fills them in or removes them via the
  // Batch 9 Add/Remove controls on Step 3.
  while (techs.length < page2.num_technicians) {
    techs.push({
      name: '',
      skill_tier: 5,
      efficiency_percentage: 100,
      buffer_percentage: null,
      shift_start: '08:00',
      shift_end: '17:00',
      bays_allocated: 1,
      bay_types: ['flat'] as BayTypeEnum[],
    });
  }
  // Trim if the roster produced more techs than the page-2 ceiling.
  techs.length = page2.num_technicians;

  // Parking — only prefill when the heuristic is above the
  // confidence threshold; otherwise let the wizard default.
  const parkingInference = inferences.find((i) => i.inference_kind === 'parking_heuristic');
  let parkingSpotCount = 0;
  if (parkingInference && parkingInference.confidence >= CONFIDENCE_THRESHOLD) {
    const payload = parkingInference.payload as unknown as ParkingPayload;
    parkingSpotCount = clamp(payload.suggestedSpotCount ?? 0, 0, 500);
  }

  // Batch 9B: seed advisor names from the imported CSV's distinct
  // `advisor_display_name` values. Caller (OnboardSetupImportWizard)
  // queries shop_history_repair_orders directly — no inference layer
  // change required. Manager names are not represented in the CSV
  // contract, so manager rows stay blank.
  const advisorTarget = Math.max(0, page2.num_advisors);
  const advisorRows = Array.from({ length: advisorTarget }, (_, idx) => ({
    team_role: 'SERVICE_WRITER' as const,
    name: advisorNames[idx]?.trim() ?? '',
  }));
  const managerRows = Array.from({ length: Math.max(0, page2.num_managers) }, () => ({
    team_role: 'MANAGER' as const,
    name: '',
  }));

  // No specialty inference today — leave empty so the wizard's
  // specialty section is opt-in. (A future slice can detect
  // "lots of ADAS calibrations in service_mix" → suggest an ADAS
  // bay specialty.)
  return {
    technicians: [...techs, ...advisorRows, ...managerRows],
    specialties: [] as Array<{ kind: SpecialtyKind; primary_tech_index: number }>,
    parking_spot_count: parkingSpotCount,
  };
}

// =================================================================
// Public API
// =================================================================

/**
 * Batch 9C: bundle returned alongside the wizard initial. Carries
 * the FULL inferred roster (technicians + advisors) so the wizard's
 * Step 3 can top up rows from real names when Step 2 counts
 * increase past the initially-displayed slice. Pre-9C, anything
 * beyond `page2.num_technicians` was discarded at `buildPage3`
 * time — bumping the count later only ever produced blank rows.
 */
export interface WizardInitialWithRoster {
  initial: Partial<WizardData>;
  /**
   * All inferred technician display names, ordered by job_count
   * descending. Includes the names already projected into
   * `initial.page3.technicians`; the wizard de-duplicates by name
   * when picking the next "unused" suggestion.
   */
  suggestedTechnicianRoster: string[];
  /**
   * All distinct imported advisor display names, ordered by
   * frequency descending. Same de-dup contract as the technician
   * roster above.
   */
  suggestedAdvisorRoster: string[];
}

export function buildWizardInitialFromInferences(
  input: InferenceLoaderInput,
  // Batch 8B: optional pre-existing shop hours that should take
  // precedence over CSV inference (typically set by the public-
  // listing lookup in `lookup-shop-public-info`). Caller fetches
  // shop_profile and passes the hours map + public_hours_source.
  existingHours?: BuildPage1ExistingHours,
): Partial<WizardData> {
  return buildWizardInitialWithRoster(input, existingHours).initial;
}

/**
 * Batch 9C extended entry point: returns the same wizard initial
 * `buildWizardInitialFromInferences` produces, plus the full
 * suggested rosters the wizard's Step 3 reconciler uses to top up
 * rows when counts increase. Existing callers that only need the
 * wizard initial continue to use `buildWizardInitialFromInferences`.
 */
export function buildWizardInitialWithRoster(
  input: InferenceLoaderInput,
  existingHours?: BuildPage1ExistingHours,
): WizardInitialWithRoster {
  const page1 = buildPage1(input.shopName, input.timeZone, input.inferences, existingHours);
  const page2 = buildPage2(input.inferences);
  const page3 = buildPage3(
    input.inferences,
    input.technicianProfiles,
    page2,
    input.advisorNames ?? [],
  );
  // Extract the full ordered technician roster from the inference
  // payload so Step 3 can top up from real names. Sorted by
  // jobCount desc to match what the inference review card shows.
  const rosterInference = input.inferences.find((i) => i.inference_kind === 'technician_roster');
  let suggestedTechnicianRoster: string[] = [];
  if (rosterInference) {
    const payload = rosterInference.payload as unknown as TechRosterPayload;
    suggestedTechnicianRoster = [...(payload.technicians ?? [])]
      .sort((a, b) => (b.jobCount ?? 0) - (a.jobCount ?? 0))
      .map((t) => (t.displayName ?? '').trim())
      .filter((n) => n.length > 0);
  }
  return {
    initial: { page1, page2, page3 },
    suggestedTechnicianRoster,
    suggestedAdvisorRoster: (input.advisorNames ?? [])
      .map((n) => n.trim())
      .filter((n) => n.length > 0),
  };
}

/**
 * Convenience indicator — true iff at least one inference passed
 * the confidence threshold and contributed to the prefill. Used by
 * the UI to decide whether to show "we prefilled X from Y rows"
 * vs the silent fallback "your import was low-confidence; please
 * fill in manually" copy.
 */
export function inferencesContributedToPrefill(input: InferenceLoaderInput): boolean {
  return input.inferences.some((i) => i.confidence >= CONFIDENCE_THRESHOLD);
}

export const _internal = { CONFIDENCE_THRESHOLD, DAY_KEYS, buildPage1, buildPage3 };
